import { Table, TableColumns, Tooltip } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { naturalStringSort } from 'utils/stringHelper';

const PlanungImportEventsTableFragment = graphql(`
  fragment PlanungImportEventsTableFields on Query {
    calendarEvents(where: $eventWhere, options: $eventOptions) {
      __typename
      uuid
      title
      start
      classShortNames
      duration
      days {
        __typename
        uuid
        date
      }
      locations {
        __typename
        name
        description
        uuid
      }
      holder {
        ... on Group {
          __typename
          uuid
          displayName
          subject {
            uuid
            name
          }
        }
        ... on Organization {
          __typename
          uuid
          displayName
        }
        ... on Profile {
          __typename
          uuid
          displayName
        }
      }
    }
  }
`);

type PlanungImportEventsTableType = {
  uuid: string;
  start: string;
  duration: string;
  rooms: string;
  holder: string;
  subject: string;
  classesOrClassGroups: string;
};

type PlanungImportEventsTableProps = {
  query: FragmentType<typeof PlanungImportEventsTableFragment> | undefined;
};

export const PlanungImportEventsTable = ({ query }: PlanungImportEventsTableProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(PlanungImportEventsTableFragment, query);
  const events = fragment?.calendarEvents ?? [];

  const columns: TableColumns<PlanungImportEventsTableType>[] = [
    {
      id: 'start',
      accessorFn: ({ start }) => dayjs(start).utc().format('DD.MM.YYYY, HH:mm'),
      header: t('common.start'),
      size: 200,
    },
    {
      id: 'duration',
      accessorKey: 'duration',
      header: t('common.duration'),
      size: 150,
    },
    {
      id: 'subject',
      accessorKey: 'subject',
      header: t('common.subject'),
      cell({ row }) {
        const subject = row.original.subject;
        return <Tooltip content={subject}>{subject}</Tooltip>;
      },
      size: 250,
    },
    {
      id: 'holder',
      accessorKey: 'holder',
      header: t('common.holder'),
      cell({ row }) {
        const holder = row.original.holder;
        return <Tooltip content={holder}>{holder}</Tooltip>;
      },
      size: 250,
    },

    {
      id: 'rooms',
      accessorKey: 'rooms',
      header: t('rooms.title', { count: 2 }),
      cell({ row }) {
        const rooms = row.original.rooms;
        return <Tooltip content={rooms}>{rooms}</Tooltip>;
      },
      size: 250,
    },
  ];

  const data: PlanungImportEventsTableType[] = events
    .map((event) => {
      const subject = event.holder.__typename === 'Group' ? event.holder.subject : null;
      const rooms = event.locations.map((location) => location.name).join(', ');
      const duration = Number(event.duration);
      return {
        uuid: event.uuid,
        start: event.start,
        duration: `${(isNaN(duration) ? 0 : duration) / 60} ${t('common.minutes', { count: duration })}`,
        rooms,
        holder: event.holder.displayName ?? '',
        subject: subject?.name ?? '',
        classesOrClassGroups: event.classShortNames?.join(', ') ?? '',
      };
    })
    .sort((a, b) => naturalStringSort(a.classesOrClassGroups, b.classesOrClassGroups));

  return (
    <Table<PlanungImportEventsTableType>
      data={data}
      columns={columns}
      hideHeader={data.length === 0}
      customPadding='var(--spacing-7)'
      emptyStateSettings={{ title: '', subtitle: '' }}
      lastColWidth='0'
      actionBarSettings={{ showGlobalFilter: true }}
      showPagination
      pageSize={20}
    />
  );
};
